'use client';

import { useCallback, useEffect, useState } from 'react';
import type { ConsentChoices, ConsentRecord } from '@/lib/consent';
import {
  COOKIE_POLICY_VERSION,
  DEFAULT_REJECT,
  detectGpc,
  emitConsentChanged,
  getConsent,
  getOrCreateSessionId,
  saveConsent,
  shouldShowBanner,
} from '@/lib/consent';
import { fetchGeo } from '@/lib/geo';
import { apiClient } from '@/lib/api-client';
import { Logger } from '@/lib/logger';
import { clearVisitorId } from '@/lib/visitor-id';

type ConsentMode = 'banner' | 'preferences' | 'hidden';

type PersistMeta = {
  country?: string | null
  gpcSignal?: boolean
};

export function useConsent() {
  const [mode, setMode] = useState<ConsentMode>('hidden');
  const [gpcSignal, setGpcSignal] = useState(false);
  const [country, setCountry] = useState<string | null>(null);

  const persistConsent = useCallback(async (choices: ConsentChoices, meta: PersistMeta = {}) => {
    const effectiveGpc = meta.gpcSignal ?? gpcSignal;
    const effectiveCountry = meta.country === undefined ? country : meta.country;
    const record: ConsentRecord = {
      version: COOKIE_POLICY_VERSION,
      choices,
      gpcSignal: effectiveGpc,
      timestamp: new Date().toISOString(),
    };

    const stored = saveConsent(record);
    emitConsentChanged(record);
    setMode('hidden');

    // Withdrawing analytics consent must also drop the persistent visitor ID,
    // otherwise a cross-session identifier would outlive the consent for it.
    if (!choices.analytics) {
      clearVisitorId();
    }

    if (!stored) {
      Logger.warn('Cookie consent could not be persisted to localStorage');
    }

    try {
      await apiClient.consent.logCookies({
        choices,
        consent_version: COOKIE_POLICY_VERSION,
        gpc_signal: effectiveGpc,
        session_id: getOrCreateSessionId(),
        ...(effectiveCountry ? { ip_country: effectiveCountry } : {}),
      });
    } catch (error) {
      Logger.warn('Cookie consent audit mirror failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [country, gpcSignal]);

  useEffect(() => {
    let cancelled = false;

    const decide = async () => {
      const existing = getConsent();
      if (existing) return;

      const geo = await fetchGeo();
      const serverCountry = geo?.country ?? null;
      const gpc = detectGpc() || Boolean(geo?.gpc);
      if (cancelled) return;

      // Re-read consent after the await: the visitor may have chosen while the
      // geo lookup was in flight (e.g. via the footer "Cookie settings" link).
      // Without this the banner would pop back up over an already-made choice.
      const chosenMeanwhile = getConsent();
      if (chosenMeanwhile) return;

      setCountry(serverCountry);
      setGpcSignal(gpc);

      if (gpc) {
        await persistConsent(DEFAULT_REJECT, { country: serverCountry, gpcSignal: true });
        return;
      }

      if (shouldShowBanner({ country: serverCountry, gpcSignal: gpc, hasExistingConsent: false })) {
        setMode('banner');
      }
    };

    decide();

    return () => {
      cancelled = true;
    };
  }, [persistConsent]);

  return {
    mode,
    setMode,
    gpcSignal,
    country,
    persistConsent,
  };
}
